import type { SpectralSignature } from '../scan/spectral-signature.js';
import type { PerBandEnvelopeResult } from '../scan/per-band-envelope.js';

const PANEL_ID = 'spectralSignaturePanel';

function el(): HTMLElement | null {
  return document.getElementById(PANEL_ID);
}

export function clearSpectralPanel(): void {
  const root = el();
  if (!root) return;
  root.innerHTML = '<div class="spectral-empty small">No spectral data yet. Run a Ping with a detected echo.</div>';
}

export function drawSpectralPanel(
  sig: SpectralSignature | null | undefined,
  perBand?: PerBandEnvelopeResult | null,
): void {
  const root = el();
  if (!root) return;
  if (!sig || sig.bands.length === 0 || sig.totalEnergy <= 0) {
    clearSpectralPanel();
    return;
  }

  let maxAmp = 0;
  for (const b of sig.bands) if (b.amplitude > maxAmp) maxAmp = b.amplitude;
  if (maxAmp <= 0) {
    clearSpectralPanel();
    return;
  }

  // Build a lookup from band id to per-band coherence so we can render a
  // matching coherence cell. When the per-band entry is missing we render
  // a placeholder dash instead of breaking.
  const coherenceById = new Map<string, number>();
  if (perBand && perBand.bands.length > 0) {
    for (const b of perBand.bands) {
      coherenceById.set(b.id, b.phaseCoherence);
    }
  }

  const parts: string[] = [];
  parts.push(
    `<div class="spectral-dominant">Dominant: <strong>${escapeHtml(sig.dominantBandId)}</strong> &middot; total energy: ${sig.totalEnergy.toExponential(2)}</div>`,
  );
  for (const band of sig.bands) {
    const fillPct = Math.max(0, Math.min(100, (band.amplitude / maxAmp) * 100));
    const isDominant = band.id === sig.dominantBandId;
    const fillCls = isDominant ? 'spectral-band-bar-fill is-dominant' : 'spectral-band-bar-fill';
    const snrStr = Number.isFinite(band.snr) ? band.snr.toFixed(1) : '-';
    const coh = coherenceById.get(band.id);
    const cohStr = (coh !== undefined && Number.isFinite(coh)) ? coh.toFixed(2) : '—';
    const ariaLabel = `${band.id} band amplitude ${fillPct.toFixed(0)} percent, ${formatHz(band.fLow)} to ${formatHz(band.fHigh)} hertz, SNR ${snrStr}`;
    parts.push(
      `<div class="spectral-band-row">`
        + `<span class="spectral-band-id">${escapeHtml(band.id)}</span>`
        + `<span class="spectral-band-bar-bg"`
          + ` role="progressbar"`
          + ` aria-valuenow="${fillPct.toFixed(0)}"`
          + ` aria-valuemin="0"`
          + ` aria-valuemax="100"`
          + ` aria-label="${escapeHtml(ariaLabel)}">`
          + `<span class="${fillCls}" style="width:${fillPct.toFixed(1)}%"></span>`
        + `</span>`
        + `<span class="spectral-band-meta">${formatHz(band.fLow)}-${formatHz(band.fHigh)} &middot; SNR ${snrStr} &middot; <span class="spectral-band-coh">coh: ${cohStr}</span></span>`
        + `</div>`,
    );
  }
  root.innerHTML = parts.join('');
}

function formatHz(hz: number): string {
  if (!Number.isFinite(hz)) return '-';
  if (hz >= 1000) return `${(hz / 1000).toFixed(1)}k`;
  return `${hz.toFixed(0)}`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}
